/**
 * Contato com a WebAuthn do navegador. As opções chegam da API em JSON e a resposta volta
 * em JSON (`parse*FromJSON` / `toJSON()`), sem biblioteca extra.
 */

/** O usuário fechou o leitor biométrico (ou o tempo acabou). Quem chama sai em silêncio. */
export class BiometriaCancelledError extends Error {
  constructor() {
    super('Confirmação por biometria cancelada.')
    this.name = 'BiometriaCancelledError'
  }
}

export function isBiometriaCancelled(error: unknown): error is BiometriaCancelledError {
  return error instanceof BiometriaCancelledError
}

/** Erro do navegador com mensagem pronta para exibir (não vem da API). */
export class BiometriaNavegadorError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'BiometriaNavegadorError'
  }
}

/** Exige contexto seguro (HTTPS ou localhost) e os helpers JSON da WebAuthn. */
export function suportaBiometria(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof PublicKeyCredential.parseCreationOptionsFromJSON === 'function' &&
    typeof PublicKeyCredential.parseRequestOptionsFromJSON === 'function' &&
    !!navigator.credentials
  )
}

function traduzirErro(error: unknown): Error {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
      return new BiometriaCancelledError()
    }
    if (error.name === 'InvalidStateError') {
      return new BiometriaNavegadorError('Este aparelho já está cadastrado.')
    }
    if (error.name === 'SecurityError') {
      return new BiometriaNavegadorError(
        'O navegador recusou a biometria neste endereço. Use o endereço oficial do sistema.',
      )
    }
  }
  return error instanceof Error ? error : new Error(String(error))
}

function exigirCredencial(credencial: Credential | null): PublicKeyCredential {
  if (!(credencial instanceof PublicKeyCredential)) throw new BiometriaCancelledError()
  return credencial
}

export async function criarCredencial(
  opcoes: PublicKeyCredentialCreationOptionsJSON,
): Promise<RegistrationResponseJSON> {
  try {
    const credencial = await navigator.credentials.create({
      publicKey: PublicKeyCredential.parseCreationOptionsFromJSON(opcoes),
    })
    return exigirCredencial(credencial).toJSON() as RegistrationResponseJSON
  } catch (error) {
    throw traduzirErro(error)
  }
}

export async function obterAssercao(
  opcoes: PublicKeyCredentialRequestOptionsJSON,
): Promise<AuthenticationResponseJSON> {
  try {
    const assercao = await navigator.credentials.get({
      publicKey: PublicKeyCredential.parseRequestOptionsFromJSON(opcoes),
    })
    return exigirCredencial(assercao).toJSON() as AuthenticationResponseJSON
  } catch (error) {
    throw traduzirErro(error)
  }
}

/** Nome inicial do aparelho no cadastro (o usuário pode trocar). */
export function sugerirNomeDispositivo(userAgent = navigator.userAgent): string {
  if (/iPhone/i.test(userAgent)) return 'IPHONE'
  if (/iPad/i.test(userAgent)) return 'IPAD'
  if (/Android/i.test(userAgent)) {
    return /Mobile/i.test(userAgent) ? 'CELULAR ANDROID' : 'TABLET ANDROID'
  }
  if (/Windows/i.test(userAgent)) return 'COMPUTADOR WINDOWS'
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'COMPUTADOR MAC'
  if (/Linux/i.test(userAgent)) return 'COMPUTADOR LINUX'
  return 'MEU APARELHO'
}
